import { DashStyleValue } from 'highcharts';

import { AiQaCardsResponse } from '../ai-qa/types';
import { dashboardApi } from '../dashboard-api';
import {
  ASSIST_INSIGHTS_BASE,
  DEFAULT_INSIGHT_PAGE_SIZE,
  INSIGHTS_ROUTER_BASE,
} from './constants';
import { mockQuickFeedbackResponse } from './mock';
import {
  ActionRecordsRequest,
  ActionRecordsResponse,
  AgentAiMetricCardsRequest,
  AgentAiMetricReasonsRequest,
  AgentInfoRequest,
  AgentInfoResponse,
  AgentQaConfigurationRankRequest,
  AgentQaConfigurationTestResponse,
  AgentQaScorecardUpdateRequest,
  AgentQaSummaryRequest,
  AgentStatsRequest,
  AgentStatsResponse,
  AgentsTicketComparisonRequest,
  AgentsTicketRequest,
  AgentsTicketsTableRequest,
  AgentsTicketsTableResponse,
  AgentTicketMetadataValueOptionRequest,
  AgentTicketsTableRequest,
  AgentTicketsTableResponse,
  AgentTopicRequest,
  AgentTrendsRequest,
  AiQaConfig,
  AiQaConfigResponse,
  AiQaConfigurationPayloadRequest,
  AiQaConfigurationsRequest,
  AiQaConfigurationsV2Request,
  AiQaConfigurationTestPayloadRequest,
  AiQaInsightCardsRequest,
  AiQaSettingResponse,
  AiQaSettingsColorThresholdRequest,
  ArticleContentRequest,
  ArticleCsatResponse,
  ArticleDetailHeaderRequest,
  ArticleDetailResponse,
  ArticleLineGraphRequest,
  ArticlePerformanceRequest,
  ArticlePerformanceResponse,
  ArticleTableRequest,
  ArticleTableResponse,
  ArticleTopMetricRequest,
  ArticleUserFeedbackResponse,
  AssistBaseFilterRequest,
  AssistChatTableRequest,
  AssistChatTableResponse,
  AssistEventCardOptionsResponse,
  AssistOverviewAgentsRequest,
  AssistOverviewAgentsResponse,
  AssistOverviewArticlesRequest,
  AssistOverviewArticlesResponse,
  AssistOverviewFilterOptionsResponse,
  AssistOverviewRequest,
  AssistOverviewResponse,
  AvailableEvent,
  BaseInsightResponse,
  ChatsSavingsInsightsRequest,
  ChatTableRequest,
  ChatTableResponse,
  CommonCsatResponse,
  CommonTrendBarGraphResponse,
  ConversationDeeplinkResponse,
  ConversationDetailResponse,
  ConversationRequest,
  ConversationReviewRequest,
  ConversationReviewResponse,
  EmailArticleTableRequest,
  EmailArticleTableResponse,
  EmailChatsRequest,
  EmailChatTableRequest,
  EmailChatTableResponse,
  EmailNoResponseResponse,
  EngagementTrendsResponse,
  EventConfigResponse,
  FilterOptionsResponse,
  GeneratedArticleRequest,
  GraphLabelType,
  HelpdeskCsatResponse,
  ImpactInsightRequest,
  InsightAgentAiMetricCard,
  InsightAgentAiMetricReasons,
  InsightAgentReportSummary,
  InsightAgentsHeaderResponse,
  InsightAgentTopicCard,
  InsightArticle,
  InsightArticleContent,
  InsightChatsSavingsResponseWithComparison,
  InsightOverviewChatPerformance,
  InsightWorkflow,
  InsightWorkflowDetail,
  InsightWorkflowDetailChart,
  OrgFeatureQaConfigResponse,
  OutcomeTrendsResponse,
  OverallCSATRequest,
  OverviewChatTrendGraphRequest,
  OverviewOvertimeGraphRequest,
  OverviewOvertimeGraphResponse,
  PlaybookConversationDetailResponse,
  PlaybookConversationsResponse,
  ProactiveConversationsRequest,
  ProactiveConversationsResponse,
  ProactiveFunnelRequest,
  ProactiveFunnelResponse,
  QuickFeedbackInsightsRequest,
  QuickFeedbackResponse,
  RelevanceAndEngagementInsightRequest,
  ReviewProgressRequest,
  ReviewProgressResponse,
  ScheduledOutboundCallsTableRequest,
  ScheduledOutboundCallsTableResponse,
  SentimentInsightRequest,
  SentimentMetrics,
  SingleMetricImpactResponse,
  TicketTableRequest,
  TicketTableResponse,
  TimestampRequest,
  TimestampRequest2,
  TopicCardInsightResponse,
  TopicCardRequest,
  TopicCardWorstWorkflowResponse,
  TopicDetailHeaderRequest,
  TopicDetailResponse,
  TopicHeaderResponse,
  TopicInformationRequest,
  TopicInformationResponse,
  TopicLineGraphRequest,
  TopicsEngagementResponse,
  TopicsRelevanceResponse,
  TopicTableRequest,
  UpdateEventConfigRequest,
  WorkflowDetailChartRequest,
  WorkflowDetailHeaderRequest,
  WorkflowTableRequest,
  WorkflowTableResponse,
  WorkflowTopMetricRequest,
} from './types';
import _omit from 'lodash/fp/omit';
import { InsightTopicTableResponse } from 'src/components/dashboard-pages/solve-insights/topics/types';
import { DiscoverArticle } from 'src/reducers/discoverReducer/types';
import {
  setGeneratedArticles,
  setIsLoadingGeneratedArticles,
} from 'src/slices/solve-insights/solveInsightsSlice';

export const insightsApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    createAiQaConfig: builder.mutation<
      AiQaConfig,
      AiQaConfigurationPayloadRequest
    >({
      invalidatesTags: ['AgentQaConfigs'],
      query: params => ({
        body: params,
        method: 'POST',
        url: INSIGHTS_ROUTER_BASE + '/ai-qa-configuration',
      }),
      transformResponse: (response: { data: AiQaConfig }) => response.data,
    }),
    deleteAiQaConfig: builder.mutation<void, { model_id: string }>({
      invalidatesTags: ['AgentQaConfigs'],
      query: params => ({
        body: params,
        method: 'DELETE',
        url: `${INSIGHTS_ROUTER_BASE}/ai-qa-configuration/${params.model_id}`,
      }),
    }),
    getAgentAiMetricCards: builder.query<
      InsightAgentAiMetricCard[],
      AgentAiMetricCardsRequest
    >({
      providesTags: ['AgentQaSettings'],
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/agent-ai-metric-cards',
      }),
    }),
    getAgentAiMetricCardsV2: builder.query<
      InsightAgentAiMetricCard[],
      AgentAiMetricCardsRequest
    >({
      providesTags: ['AgentQaSettings'],
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/agent-ai-metric-cards-v2',
      }),
    }),
    getAgentAiMetricReasons: builder.query<
      InsightAgentAiMetricReasons,
      AgentAiMetricReasonsRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/ai-metric-card-reasons',
      }),
    }),
    getAgentQaAccess: builder.query<boolean, void>({
      query: () => ({
        url: INSIGHTS_ROUTER_BASE + '/agent-qa-access',
      }),
    }),
    getAgentsHeader: builder.query<
      InsightAgentsHeaderResponse,
      AgentsTicketRequest
    >({
      providesTags: ['AgentQaSettings'],
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/agents-header',
      }),
    }),
    getAgentsPerformanceChart: builder.query<
      OverviewOvertimeGraphResponse,
      AgentsTicketComparisonRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/agents-performance-chart',
      }),
    }),
    getAgentsPerformanceChartV2: builder.query<
      OverviewOvertimeGraphResponse,
      AgentsTicketComparisonRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/agents-performance-chart-v2',
      }),
    }),
    getAgentsTicketsTable: builder.query<
      AgentsTicketsTableResponse,
      AgentsTicketsTableRequest
    >({
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      merge: (currentCache, newItems) => {
        currentCache.nextPage = newItems.metadata.next_page !== null;
        currentCache.metadata = newItems.metadata;
        if (newItems.metadata.page === 1) {
          currentCache.data = [...newItems.data];
        } else {
          currentCache.data = [...currentCache.data, ...newItems.data];
        }
      },
      providesTags: ['AgentQaConfigs'],
      query: params => ({
        body: {
          ...params,
          page_size: DEFAULT_INSIGHT_PAGE_SIZE,
        },
        method: 'POST',
        url: INSIGHTS_ROUTER_BASE + '/agents-tickets-table',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        // Remove page from cache key so we maintain the same cache when fetching next page
        return _omit(['page'], queryArgs);
      },
    }),
    getAgentTicketMetadataKeyOptions: builder.query<
      FilterOptionsResponse,
      void
    >({
      query: () => ({
        url: INSIGHTS_ROUTER_BASE + '/agent-ticket-metadata-key-options',
      }),
    }),
    getAgentTicketMetadataValueOptions: builder.query<
      FilterOptionsResponse,
      AgentTicketMetadataValueOptionRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/agent-ticket-metadata-value-options',
      }),
    }),
    getAgentTicketsTable: builder.query<
      AgentTicketsTableResponse,
      AgentTicketsTableRequest
    >({
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      merge: (currentCache, newItems) => {
        currentCache.nextPage = newItems.metadata.next_page !== null;
        currentCache.metadata = newItems.metadata;
        if (newItems.metadata.page === 1) {
          currentCache.data = [...newItems.data];
        } else {
          currentCache.data = [...currentCache.data, ...newItems.data];
        }
      },
      query: params => ({
        body: {
          ...params,
          page_size: DEFAULT_INSIGHT_PAGE_SIZE,
        },
        method: 'POST',
        url: INSIGHTS_ROUTER_BASE + '/agent-tickets-table',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        // Remove page from cache key so we maintain the same cache when fetching next page
        return _omit(['page'], queryArgs);
      },
    }),
    getAgentTopics: builder.query<InsightAgentTopicCard[], AgentTopicRequest>({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/agent-topic-data-v2',
      }),
    }),
    getAiQaConfigs: builder.query<
      AiQaConfigResponse,
      AiQaConfigurationsRequest
    >({
      providesTags: ['AgentQaConfigs'],
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/ai-qa-configurations',
      }),
    }),
    getAiQaInsightCards: builder.query<
      AiQaCardsResponse,
      AiQaInsightCardsRequest
    >({
      query: params => ({
        body: params,
        method: 'POST',
        url: INSIGHTS_ROUTER_BASE + '/ai-qa-cards',
      }),
    }),
    getAiQaSettings: builder.query<AiQaSettingResponse, void>({
      providesTags: ['AgentQaConfigs', 'AgentQaSettings'],
      query: () => ({
        url: INSIGHTS_ROUTER_BASE + '/ai-qa-settings',
      }),
    }),
    getAiReportSummary: builder.query<
      InsightAgentReportSummary,
      AgentQaSummaryRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/ai-report-summary',
      }),
    }),
    getArticle: builder.query<InsightArticleContent, ArticleContentRequest>({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/article-content',
      }),
    }),
    getArticleDetailGraph: builder.query<
      ArticleDetailResponse,
      ArticleLineGraphRequest
    >({
      query: params => ({
        params: {
          ...params,
          period: params.period.toLowerCase(),
        },
        url: INSIGHTS_ROUTER_BASE + '/article-detail-chart',
      }),
    }),
    getArticleDetailHeader: builder.query<
      InsightArticle,
      ArticleDetailHeaderRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/article-detail-header',
      }),
    }),
    getArticleIntegrationOptions: builder.query<FilterOptionsResponse, void>({
      query: () => ({
        url: INSIGHTS_ROUTER_BASE + '/article-integration-options',
      }),
    }),
    getArticlePerformance: builder.query<
      ArticlePerformanceResponse,
      ArticlePerformanceRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/article-performance',
      }),
    }),
    getArticleTable: builder.query<ArticleTableResponse, ArticleTableRequest>({
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      merge: (currentCache, newItems) => {
        currentCache.nextPage = newItems.metadata.next_page !== null;
        currentCache.metadata = newItems.metadata;
        if (newItems.metadata.page === 1) {
          currentCache.data = [...newItems.data];
        } else {
          currentCache.data = [...currentCache.data, ...newItems.data];
        }
      },
      query: params => ({
        body: {
          ...params,
          page_size: DEFAULT_INSIGHT_PAGE_SIZE,
        },
        method: 'POST',
        url: INSIGHTS_ROUTER_BASE + '/articles-table',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        // Remove page from cache key so we maintain the same cache when fetching next page
        return _omit(['page'], queryArgs);
      },
    }),
    getArticleUserFeedback: builder.query<
      ArticleUserFeedbackResponse,
      TimestampRequest
    >({
      query: ({ end_timestamp, start_timestamp }) => ({
        params: {
          end_timestamp,
          start_timestamp,
        },
        url: INSIGHTS_ROUTER_BASE + '/article-user-feedback',
      }),
    }),
    getAssistActionRecords: builder.query<
      ActionRecordsResponse,
      ActionRecordsRequest
    >({
      providesTags: ['AssistActionClassification'],
      query: body => ({
        body,
        method: 'POST',
        url: ASSIST_INSIGHTS_BASE + '/action-records',
      }),
    }),
    getAssistAgentEngagementTrends: builder.query<
      EngagementTrendsResponse,
      AgentTrendsRequest
    >({
      providesTags: ['AssistActionClassification'],
      query: params => ({
        params,
        url: ASSIST_INSIGHTS_BASE + '/agent-engagement-trends',
      }),
    }),
    getAssistAgentInfo: builder.query<AgentInfoResponse, AgentInfoRequest>({
      providesTags: ['AssistActionClassification'],
      query: params => ({
        params,
        url: ASSIST_INSIGHTS_BASE + '/agent-info',
      }),
    }),
    getAssistAgentOutcomeTrends: builder.query<
      OutcomeTrendsResponse,
      AgentTrendsRequest
    >({
      providesTags: ['AssistActionClassification'],
      query: params => ({
        params,
        url: ASSIST_INSIGHTS_BASE + '/agent-outcome-trends',
      }),
    }),
    getAssistAgentStats: builder.query<AgentStatsResponse, AgentStatsRequest>({
      providesTags: ['AssistActionClassification'],
      query: params => ({
        params,
        url: ASSIST_INSIGHTS_BASE + '/agent-stats',
      }),
    }),
    getAssistAvailableEvents: builder.query<AvailableEvent[], void>({
      query: () => ({
        url: ASSIST_INSIGHTS_BASE + '/available-events',
      }),
    }),
    getAssistCardBuilderOptions: builder.query<
      AssistEventCardOptionsResponse,
      void
    >({
      query: () => ({
        url: ASSIST_INSIGHTS_BASE + '/card-builder-options',
      }),
    }),
    getAssistConversationDetail: builder.query<
      PlaybookConversationDetailResponse,
      ConversationRequest
    >({
      query: ({ conversation_id }) => ({
        params: {
          conversation_id,
        },
        url: INSIGHTS_ROUTER_BASE + '/playbook-conversation-detail',
      }),
    }),
    getAssistConversations: builder.query<
      PlaybookConversationsResponse,
      AssistBaseFilterRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/playbook-conversations',
      }),
    }),
    getAssistConversationTable: builder.query<
      AssistChatTableResponse,
      AssistChatTableRequest
    >({
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      merge: (currentCache, newItems) => {
        currentCache.nextPage = newItems.metadata.next_page !== null;
        currentCache.metadata = newItems.metadata;
        if (newItems.metadata.page === 1) {
          currentCache.data = [...newItems.data];
        } else {
          currentCache.data = [...currentCache.data, ...newItems.data];
        }
      },
      query: params => ({
        body: {
          ...params,
          page_size: DEFAULT_INSIGHT_PAGE_SIZE,
        },
        method: 'POST',
        url: INSIGHTS_ROUTER_BASE + '/playbook-conversations-table',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        // Remove page from cache key so we maintain the same cache when fetching next page
        return _omit(['page'], queryArgs);
      },
    }),
    getAssistEventConfig: builder.query<EventConfigResponse, void>({
      providesTags: ['AssistActionClassification'],
      query: () => ({
        url: ASSIST_INSIGHTS_BASE + '/event-config',
      }),
    }),
    getAssistFcrImpact: builder.query<
      SingleMetricImpactResponse,
      ImpactInsightRequest
    >({
      providesTags: ['AssistActionClassification'],
      query: body => ({
        body,
        method: 'POST',
        url: ASSIST_INSIGHTS_BASE + '/impact/fcr',
      }),
    }),
    getAssistFeedback: builder.query<BaseInsightResponse, TimestampRequest>({
      query: () => ({
        url: '/org-config',
      }),
      transformResponse: () => {
        return mockQuickFeedbackResponse as unknown as BaseInsightResponse;
      },
    }),
    getAssistFirstResponseTimeImpact: builder.query<
      SingleMetricImpactResponse,
      ImpactInsightRequest
    >({
      providesTags: ['AssistActionClassification'],
      query: body => ({
        body,
        method: 'POST',
        url: ASSIST_INSIGHTS_BASE + '/impact/first-response-time',
      }),
    }),
    getAssistGeneratedReply: builder.query<
      BaseInsightResponse,
      AssistBaseFilterRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/playbook-reply-usage',
      }),
    }),
    getAssistInteractions: builder.query<
      BaseInsightResponse,
      AssistBaseFilterRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/playbook-ai-interaction',
      }),
    }),
    getAssistOverview: builder.query<
      AssistOverviewResponse,
      AssistOverviewRequest
    >({
      providesTags: ['AssistActionClassification'],
      query: body => ({
        body,
        method: 'POST',
        url: ASSIST_INSIGHTS_BASE + '/overview',
      }),
    }),
    getAssistOverviewAgents: builder.query<
      AssistOverviewAgentsResponse,
      AssistOverviewAgentsRequest
    >({
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      merge: (currentCache, newItems) => {
        currentCache.metadata = newItems.metadata;
        if ((newItems.metadata?.page ?? 1) === 1) {
          currentCache.data = [...newItems.data];
        } else {
          currentCache.data = [...currentCache.data, ...newItems.data];
        }
      },
      providesTags: ['AssistActionClassification'],
      query: body => ({
        body,
        method: 'POST',
        url: ASSIST_INSIGHTS_BASE + '/agents',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        return _omit(['page'], queryArgs);
      },
    }),
    getAssistOverviewArticles: builder.query<
      AssistOverviewArticlesResponse,
      AssistOverviewArticlesRequest
    >({
      providesTags: ['AssistActionClassification'],
      query: body => ({
        body,
        method: 'POST',
        url: ASSIST_INSIGHTS_BASE + '/articles',
      }),
    }),
    getAssistOverviewFilterOptions: builder.query<
      AssistOverviewFilterOptionsResponse,
      TimestampRequest2
    >({
      query: params => ({
        params,
        url: ASSIST_INSIGHTS_BASE + '/filter-options',
      }),
    }),
    getAssistRepliesImpact: builder.query<
      SingleMetricImpactResponse,
      ImpactInsightRequest
    >({
      providesTags: ['AssistActionClassification'],
      query: body => ({
        body,
        method: 'POST',
        url: ASSIST_INSIGHTS_BASE + '/impact/replies',
      }),
    }),
    getAssistResolutionTimeImpact: builder.query<
      SingleMetricImpactResponse,
      ImpactInsightRequest
    >({
      providesTags: ['AssistActionClassification'],
      query: body => ({
        body,
        method: 'POST',
        url: ASSIST_INSIGHTS_BASE + '/impact/resolution-time',
      }),
    }),
    getAssistTicketTable: builder.query<
      TicketTableResponse,
      TicketTableRequest
    >({
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      merge: (currentCache, newItems) => {
        currentCache.metadata = newItems.metadata;
        if (newItems.metadata.page === 1) {
          currentCache.data = [...newItems.data];
        } else {
          currentCache.data = [...currentCache.data, ...newItems.data];
        }
      },
      providesTags: ['AssistActionClassification'],
      query: body => ({
        body,
        method: 'POST',
        url: ASSIST_INSIGHTS_BASE + '/tickets',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        return _omit(['page'], queryArgs);
      },
    }),
    getChannelOptions: builder.query<FilterOptionsResponse, void>({
      query: () => ({
        url: INSIGHTS_ROUTER_BASE + '/channel-options',
      }),
      transformResponse: (response: FilterOptionsResponse) => {
        const data = response.data.filter(
          option => option.value !== 'playbook',
        );
        return {
          data,
        };
      },
    }),
    getChatsSavings: builder.query<
      InsightChatsSavingsResponseWithComparison,
      ChatsSavingsInsightsRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/chats-savings',
      }),
    }),
    getCommonCSAT: builder.query<CommonCsatResponse, TimestampRequest>({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/conversation-csat',
      }),
    }),
    getConversation: builder.query<
      ConversationDetailResponse,
      ConversationRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/conversation-detail',
      }),
    }),
    getConversationDeeplink: builder.query<
      ConversationDeeplinkResponse,
      string
    >({
      query: conversationId => ({
        url: `${INSIGHTS_ROUTER_BASE}/conversation/${conversationId}/deeplink`,
      }),
    }),
    getConversationReview: builder.query<
      ConversationReviewResponse | null,
      ConversationRequest
    >({
      providesTags: (_result, _error, { conversation_id }) => [
        { id: conversation_id, type: 'ConversationReview' },
      ],
      query: ({ conversation_id }) => ({
        url: `${INSIGHTS_ROUTER_BASE}/conversation-reviews/${conversation_id}`,
      }),
    }),
    getConversationTable: builder.query<ChatTableResponse, ChatTableRequest>({
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      merge: (currentCache, newItems) => {
        currentCache.nextPage = newItems.metadata.next_page !== null;
        currentCache.metadata = newItems.metadata;
        if (newItems.metadata.page === 1) {
          currentCache.data = [...newItems.data];
        } else {
          currentCache.data = [...currentCache.data, ...newItems.data];
        }
      },
      providesTags: [{ id: 'LIST', type: 'ConversationReview' }],
      query: params => ({
        body: {
          ...params,
          // Callers that only need existence (e.g. a deep-link gate) can pass a
          // small page_size to avoid hydrating a full page of conversation rows.
          page_size: params.page_size ?? DEFAULT_INSIGHT_PAGE_SIZE,
        },
        method: 'POST',
        url: INSIGHTS_ROUTER_BASE + '/chats-table',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        // Remove page from cache key so we maintain the same cache when fetching next page
        return _omit(['page'], queryArgs);
      },
    }),
    getCSAT: builder.query<ArticleCsatResponse, ArticleTopMetricRequest>({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/csat',
      }),
    }),
    getEmailArticleTable: builder.query<
      EmailArticleTableResponse,
      EmailArticleTableRequest
    >({
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      merge: (currentCache, newItems) => {
        currentCache.nextPage = newItems.metadata.next_page !== null;
        currentCache.metadata = newItems.metadata;
        if (newItems.metadata.page === 1) {
          currentCache.data = [...newItems.data];
        } else {
          currentCache.data = [...currentCache.data, ...newItems.data];
        }
      },
      query: params => ({
        body: {
          ...params,
          page_size: DEFAULT_INSIGHT_PAGE_SIZE,
        },
        method: 'POST',
        url: INSIGHTS_ROUTER_BASE + '/email-articles-table',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        // Remove page from cache key so we maintain the same cache when fetching next page
        return _omit(['page'], queryArgs);
      },
    }),
    getEmailChatsSavings: builder.query<
      InsightChatsSavingsResponseWithComparison,
      EmailChatsRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/email-chats-savings',
      }),
    }),
    getEmailConversationTable: builder.query<
      EmailChatTableResponse,
      EmailChatTableRequest
    >({
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      merge: (currentCache, newItems) => {
        currentCache.nextPage = newItems.metadata.next_page !== null;
        currentCache.metadata = newItems.metadata;
        if (newItems.metadata.page === 1) {
          currentCache.data = [...newItems.data];
        } else {
          currentCache.data = [...currentCache.data, ...newItems.data];
        }
      },
      query: params => ({
        body: {
          ...params,
          page_size: DEFAULT_INSIGHT_PAGE_SIZE,
        },
        method: 'POST',
        url: INSIGHTS_ROUTER_BASE + '/email-chats-table',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        // Remove page from cache key so we maintain the same cache when fetching next page
        return _omit(['page'], queryArgs);
      },
    }),
    getEmailFeedback: builder.query<QuickFeedbackResponse, EmailChatsRequest>({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/email-feedback',
      }),
    }),
    getEmailHelpdeskCsat: builder.query<
      HelpdeskCsatResponse,
      EmailChatsRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/email-helpdesk-csat',
      }),
    }),
    getEmailNoResponse: builder.query<
      EmailNoResponseResponse,
      EmailChatsRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/email-no-response-cause',
      }),
    }),
    getExcludedRubrics: builder.query<string[], void>({
      providesTags: ['AgentQaConfigs'],
      query: () => ({
        url: INSIGHTS_ROUTER_BASE + '/excluded-rubrics',
      }),
    }),
    getGeneratedArticles: builder.mutation<
      { articles: Array<DiscoverArticle> },
      GeneratedArticleRequest
    >({
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        dispatch(setIsLoadingGeneratedArticles(true));
        try {
          const { data } = await queryFulfilled;
          dispatch(setGeneratedArticles(data.articles));
        } catch (e) {
          dispatch(setGeneratedArticles([]));
        } finally {
          dispatch(setIsLoadingGeneratedArticles(false));
        }
      },
      query: ({ is_parent_topic, topic_id }) => ({
        keepUnusedDataFor: 0,
        method: 'GET',
        params: {
          is_parent_topic,
          topic_id,
        },
        url: API_URL + 'ssel/discover/v1/articles/recommendations',
      }),
    }),
    getHelpdeskGroupOptions: builder.query<FilterOptionsResponse, void>({
      query: () => ({
        url: INSIGHTS_ROUTER_BASE + '/helpdesk-group-options',
      }),
    }),
    getKrWorkflowTable: builder.query<
      WorkflowTableResponse,
      WorkflowTableRequest
    >({
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      query: params => ({
        body: {
          ...params,
          page_size: DEFAULT_INSIGHT_PAGE_SIZE,
        },
        method: 'POST',
        url: INSIGHTS_ROUTER_BASE + '/kr-workflows-table',
      }),
    }),
    getOrgFeatureQaConfig: builder.query<OrgFeatureQaConfigResponse, void>({
      query: () => ({
        url: INSIGHTS_ROUTER_BASE + '/org-feature-qa-configurations',
      }),
    }),
    getOverallCSAT: builder.query<CommonCsatResponse, OverallCSATRequest>({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/overall-csat',
      }),
    }),
    getOverviewChatPerformance: builder.query<
      InsightOverviewChatPerformance,
      TimestampRequest2
    >({
      query: ({ end, start }) => ({
        params: {
          end,
          start,
        },
        url: INSIGHTS_ROUTER_BASE + '/overview-header',
      }),
    }),
    getOverviewChatsChart: builder.query<
      CommonTrendBarGraphResponse,
      OverviewChatTrendGraphRequest
    >({
      query: ({ end, period, start }) => ({
        params: {
          end,
          period,
          start,
        },
        url: INSIGHTS_ROUTER_BASE + '/overview-deflected-count-and-savings',
      }),
      transformResponse: (response: CommonTrendBarGraphResponse[]) => {
        if (response.length) {
          response.forEach(chart => {
            if (chart.type === 'overview_deflected_count_bar_chart_insight') {
              const result = { ...chart };
              result.bar.categories = ['Deflections', 'Non-deflections'];
              result.bar.values.forEach(value => {
                // TODO - Backend is returning this response incorrectly - Fix after demo
                if (typeof value.value === 'object') {
                  const valueList = value.value as unknown as number[];
                  const [def, nonDef] = valueList;
                  const deflectionObj = {
                    extended_label: 'Deflections',
                    label: 'Deflections',
                    type: 'number',
                    value: def,
                  };
                  const nonDeflectionObj = {
                    extended_label: 'Non-deflections',
                    label: 'Non-deflections',
                    type: 'number',
                    value: nonDef,
                  };
                  // eslint-disable-next-line
                  // @ts-ignore
                  value.values = [deflectionObj, nonDeflectionObj];
                }
              });
              return result;
            }
          });
        }
        return response[0];
      },
    }),
    getOverviewCsatChart: builder.query<
      CommonTrendBarGraphResponse,
      OverviewChatTrendGraphRequest
    >({
      query: ({ end, period, start }) => ({
        params: {
          end,
          period,
          start,
        },
        url: INSIGHTS_ROUTER_BASE + '/overview-csat-bar-chart',
      }),
      transformResponse: (response: CommonTrendBarGraphResponse) => {
        const result = { ...response };
        // TODO - make this change on backend
        result.bar.categories = ['CSAT'];
        return result;
      },
    }),
    getOverviewDeflectedChart: builder.query<
      CommonTrendBarGraphResponse,
      OverviewChatTrendGraphRequest
    >({
      query: ({ end, period, start }) => ({
        params: {
          end,
          period,
          start,
        },
        url: INSIGHTS_ROUTER_BASE + '/overview-deflected-bar-chart',
      }),
      transformResponse: (response: CommonTrendBarGraphResponse) => {
        const result = { ...response };
        // TODO - make this change on backend
        result.bar.categories = ['Deflection rate'];
        result.bar.values.forEach(value => (value.type = 'percentage'));
        return result;
      },
    }),
    getOverviewEngagementChart: builder.query<
      CommonTrendBarGraphResponse,
      OverviewChatTrendGraphRequest
    >({
      query: ({ end, period, start }) => ({
        params: {
          end,
          period,
          start,
        },
        url: INSIGHTS_ROUTER_BASE + '/overview-engagement-chart',
      }),
    }),
    getOverviewKASurfacedChart: builder.query<
      CommonTrendBarGraphResponse,
      OverviewChatTrendGraphRequest
    >({
      query: ({ end, period, start }) => ({
        params: {
          end,
          period,
          start,
        },
        url: INSIGHTS_ROUTER_BASE + '/overview-ka-bar-chart',
      }),
      transformResponse: (response: CommonTrendBarGraphResponse) => {
        const result = { ...response };
        // TODO - make this change on backend
        result.bar.categories = ['Knowledge article surfaced'];
        return result;
      },
    }),
    getOverviewOvertimeChart: builder.query<
      OverviewOvertimeGraphResponse,
      OverviewOvertimeGraphRequest
    >({
      query: ({ end, period, start }) => ({
        params: {
          end,
          period,
          start,
        },
        url: INSIGHTS_ROUTER_BASE + '/overview-overtime-chart',
      }),
    }),
    getOverviewQuickFeedbackChart: builder.query<
      CommonTrendBarGraphResponse,
      OverviewChatTrendGraphRequest
    >({
      query: ({ end, period, start }) => ({
        params: {
          end,
          period,
          start,
        },
        url: INSIGHTS_ROUTER_BASE + '/overview-quick-feedback-bar-chart',
      }),
      transformResponse: (response: CommonTrendBarGraphResponse) => {
        const result = { ...response };
        // TODO - make this change on backend
        result.bar.categories = ['Positive', 'Negative', 'Not answered'];
        result.bar.values.forEach(barValue => {
          barValue?.values?.forEach(value => {
            if (value.label === 'Not answered') {
              value.hidden = true;
            }
          });
        });
        return result;
      },
    }),
    getOverviewRelevanceChart: builder.query<
      CommonTrendBarGraphResponse,
      OverviewChatTrendGraphRequest
    >({
      query: ({ end, period, start }) => ({
        params: {
          end,
          period,
          start,
        },
        url: INSIGHTS_ROUTER_BASE + '/overview-relevance-chart',
      }),
    }),
    // 404s when the org lacks the `proactive_conversations` feature flag;
    // callers gate on useIsProactiveConversationsEnabled before querying.
    getProactiveConversations: builder.query<
      ProactiveConversationsResponse,
      ProactiveConversationsRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/proactive-conversations',
      }),
    }),
    getProactiveFunnel: builder.query<
      ProactiveFunnelResponse,
      ProactiveFunnelRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/proactive-funnel',
      }),
    }),
    getQuickFeedback: builder.query<
      QuickFeedbackResponse,
      QuickFeedbackInsightsRequest | AssistBaseFilterRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/feedback',
      }),
      transformResponse: (response: QuickFeedbackResponse) => {
        const result = { ...response };
        // TODO - make this change on backend

        const positiveValues = result?.misc?.positive || 0;
        const negativeValues = result?.misc?.negative || 0;
        const notAnsweredValues = result?.misc?.not_answered || 0;
        const totalFeedback =
          positiveValues + negativeValues + notAnsweredValues;

        const currentBarValues = result.bar.values || [];

        result.bar.values = currentBarValues
          .filter(barValue => barValue.label !== 'Not answered')
          .map(barValue => {
            const newBarValues =
              barValue?.values?.map?.(currentBarValue => {
                if (currentBarValue.label === 'Not answered') {
                  return {
                    ...currentBarValue,
                    hidden: true,
                  };
                }
                return currentBarValue;
              }) || [];
            if ((barValue?.tooltip_values?.length || 0) > 1) {
              return {
                ...barValue,
                tooltip_values:
                  barValue?.tooltip_values?.map?.(tooltipValue => {
                    if (tooltipValue.label === 'of overall') {
                      return {
                        ...tooltipValue,
                        value:
                          Math.round(
                            (result?.misc?.[
                              barValue?.label?.toLowerCase?.() === 'positive'
                                ? 'positive'
                                : 'negative'
                            ] /
                              (totalFeedback - notAnsweredValues) || 0) * 100,
                          ) / 100,
                      };
                    }
                    return tooltipValue;
                  }) || [],
                values: newBarValues,
              };
            }
            return {
              ...barValue,
              values: newBarValues,
            };
          });

        return result;
      },
    }),
    getReviewProgress: builder.query<
      ReviewProgressResponse,
      ReviewProgressRequest
    >({
      providesTags: [{ id: 'LIST', type: 'ConversationReview' }],
      query: params => ({
        params,
        url: `${INSIGHTS_ROUTER_BASE}/conversation-reviews/progress`,
      }),
    }),
    getScheduledOutboundCallsTable: builder.query<
      ScheduledOutboundCallsTableResponse,
      ScheduledOutboundCallsTableRequest
    >({
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      merge: (currentCache, newItems) => {
        currentCache.nextPage = newItems.metadata.next_page !== null;
        currentCache.metadata = newItems.metadata;
        if (newItems.metadata.page === 1) {
          currentCache.data = [...newItems.data];
        } else {
          currentCache.data = [...currentCache.data, ...newItems.data];
        }
      },
      query: params => ({
        body: {
          ...params,
          page_size: DEFAULT_INSIGHT_PAGE_SIZE,
        },
        method: 'POST',
        url: INSIGHTS_ROUTER_BASE + '/scheduled-outbound-calls',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        return _omit(['page'], queryArgs);
      },
    }),
    getSentimentInsight: builder.query<
      SentimentMetrics,
      SentimentInsightRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/sentiment',
      }),
    }),
    getTicketChannelOptions: builder.query<FilterOptionsResponse, void>({
      query: () => ({
        url: INSIGHTS_ROUTER_BASE + '/ticket-channel-options',
      }),
    }),
    getTicketStatusFilterOptions: builder.query<FilterOptionsResponse, void>({
      query: () => ({
        url: INSIGHTS_ROUTER_BASE + '/ticket-status-filter-options',
      }),
    }),
    getTopicCard: builder.query<
      TopicCardInsightResponse | TopicCardWorstWorkflowResponse,
      TopicCardRequest
    >({
      query: ({ card_type, end, start }) => ({
        params: {
          card_type,
          end,
          start,
        },
        url: INSIGHTS_ROUTER_BASE + '/topics-card',
      }),
    }),
    getTopicDetailGraph: builder.query<
      TopicDetailResponse,
      TopicLineGraphRequest
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        if (_arg.is_parent_topic) {
          const promises = _arg.subTopics.map(topicId =>
            fetchWithBQ(
              `${INSIGHTS_ROUTER_BASE}/topic-detail-chart?end=${
                _arg.end
              }&start=${
                _arg.start
              }&is_parent_topic=false&period=${_arg.period.toLowerCase()}&topic_id=${topicId}`,
            ),
          );
          // Handle transformations
          const promiseResults = (await Promise.all(promises)).map(
            data => data.data,
          ) as TopicDetailResponse[];
          const values: {
            dashStyle: DashStyleValue;
            label: GraphLabelType;
            topicId: string;
            value: (number | null)[];
          }[] = [];
          promiseResults.forEach((result, idx) =>
            result.line.values.forEach(value => {
              if (
                value.label === 'Chats' ||
                value.label === 'Non-deflections'
              ) {
                values.push({
                  ...value,
                  dashStyle: value.label === 'Chats' ? 'LongDash' : 'Solid',
                  topicId: _arg.subTopics[idx],
                });
              }
            }),
          );

          const result = { ...promiseResults[0] };
          result.line.values = values;

          return { data: result };
        }
        // Is subtopic
        // is_parent_topic is empty for V3 topics
        const isParentTopicParam =
          _arg.is_parent_topic !== undefined && _arg.is_parent_topic !== null
            ? `&is_parent_topic=${_arg.is_parent_topic}`
            : '';

        const result = await fetchWithBQ(
          `${INSIGHTS_ROUTER_BASE}/topic-detail-chart?end=${_arg.end}&start=${
            _arg.start
          }${isParentTopicParam}&period=${_arg.period.toLowerCase()}&topic_id=${
            _arg.topic_id
          }`,
        );
        return { data: result.data as TopicDetailResponse };
      },
    }),
    getTopicFilterOptions: builder.query<FilterOptionsResponse, void>({
      query: () => ({
        url: INSIGHTS_ROUTER_BASE + '/topic-filter-options',
      }),
    }),
    getTopicHeader: builder.query<
      TopicHeaderResponse,
      TopicDetailHeaderRequest
    >({
      query: params => ({
        params: {
          is_parent_topic: params.is_parent_topic,
          topic_id: params.topic_id,
        },
        url: `${INSIGHTS_ROUTER_BASE}${
          params.scope === 'main'
            ? '/topics-main-page-header'
            : '/topic-detail-page-header'
        }`,
      }),
    }),
    getTopicInformation: builder.query<
      TopicInformationResponse,
      TopicInformationRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/topic-information',
      }),
    }),
    getTopicsEngagement: builder.query<
      TopicsEngagementResponse,
      RelevanceAndEngagementInsightRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/engagement',
      }),
    }),
    getTopicsRelevance: builder.query<
      TopicsRelevanceResponse,
      RelevanceAndEngagementInsightRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/relevance',
      }),
    }),
    getTopicsTable: builder.query<InsightTopicTableResponse, TopicTableRequest>(
      {
        forceRefetch({ currentArg, previousArg }) {
          return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
        },
        providesTags: ['Automations'],
        query: params => ({
          body: {
            ...params,
          },
          method: 'POST',
          url: INSIGHTS_ROUTER_BASE + '/topics-table',
        }),
      },
    ),
    getWorkflowDetailChart: builder.query<
      InsightWorkflowDetailChart,
      WorkflowDetailChartRequest
    >({
      query: ({ end, period, start, workflow_id }) => ({
        params: {
          end,
          period,
          start,
          workflow_id,
        },
        url: INSIGHTS_ROUTER_BASE + '/workflow-detail-chart',
      }),
    }),
    getWorkflowDetailHeader: builder.query<
      InsightWorkflowDetail,
      WorkflowDetailHeaderRequest
    >({
      query: ({ end, start, workflow_id }) => ({
        params: {
          end,
          start,
          workflow_id,
        },
        url: INSIGHTS_ROUTER_BASE + '/workflow-detail-header',
      }),
    }),
    getWorkflowFilterOptions: builder.query<
      FilterOptionsResponse,
      TimestampRequest2
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/workflow-filter-options',
      }),
    }),
    getWorkflowOverallCSAT: builder.query<
      CommonCsatResponse,
      WorkflowTopMetricRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/workflow-overall-csat',
      }),
    }),
    getWorkflowOverallDeflections: builder.query<
      InsightChatsSavingsResponseWithComparison,
      WorkflowTopMetricRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/workflow-overall-deflections',
      }),
    }),
    getWorkflowOverallEngagement: builder.query<
      TopicsEngagementResponse,
      WorkflowTopMetricRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/workflow-overall-engagement',
      }),
    }),
    getWorkflowOverallRelevance: builder.query<
      TopicsRelevanceResponse,
      WorkflowTopMetricRequest
    >({
      query: params => ({
        params,
        url: INSIGHTS_ROUTER_BASE + '/workflow-overall-relevance',
      }),
    }),
    getWorkflowTable: builder.query<
      WorkflowTableResponse,
      WorkflowTableRequest
    >({
      forceRefetch({ currentArg, previousArg }) {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      merge: (currentCache, newItems) => {
        currentCache.nextPage = newItems.metadata.next_page !== null;
        currentCache.metadata = newItems.metadata;
        if (newItems.metadata.page === 1) {
          currentCache.data = [...newItems.data];
        } else {
          currentCache.data = [...currentCache.data, ...newItems.data];
        }
      },
      query: params => ({
        body: {
          ...params,
          page_size: DEFAULT_INSIGHT_PAGE_SIZE,
        },
        method: 'POST',
        url: INSIGHTS_ROUTER_BASE + '/workflows-table',
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        // Remove page from cache key so we maintain the same cache when fetching next page
        return _omit(['page'], queryArgs);
      },
      transformResponse: (response: WorkflowTableResponse) => {
        // Add placeholder for KR which comes from a different endpoint
        const data: InsightWorkflow[] = [
          ...response.data,
          {
            avg_csat: null,
            channel: 'n/a',
            chats: 0,
            contained_resolutions: 0,
            cost_per_ticket: 0,
            deflected_count: 0,
            dropoff_breakdown: {
              dropoff_count: 0,
              no_dropoff_count: 0,
            },
            engagement_rate: 0,
            feedback_breakdown: {
              negative: 0,
              positive: 0,
              unanswered: 0,
            },
            intent_definition_id: 'Knowledge_Article',
            intent_title: 'Knowledge Retrieval',
            intent_workflow_id: '',
            is_active: false,
            is_autoflow: false,
            is_handoff: false,
            is_loading: true,
            non_deflected_count: 0,
            potential_savings: 0,
            realized_savings: 0,
            relevance_breakdown: {
              irrelevant_count: 0,
              relevant_count: 0,
              somewhat_relevant_count: 0,
            },
            surfaced_articles: [],
            verified_resolutions: 0,
          },
        ];
        response.data = data;
        return response;
      },
    }),
    /**
     * Lightweight query to fetch AB test variant metrics from the workflows-table
     * endpoint. Returns only workflows that have ab_test_variants populated.
     */
    getWorkflowVariantMetrics: builder.query<
      InsightWorkflow[],
      { channels: string | undefined; end: number; start: number }
    >({
      query: params => ({
        body: {
          ...params,
          data_export_type: 'insight_workflows_table',
          page: 1,
          page_size: 500,
          sort_column: 'chat_count',
          sort_direction: 'DESC',
          user_query: '',
        },
        method: 'POST',
        url: INSIGHTS_ROUTER_BASE + '/workflows-table',
      }),
      transformResponse: (response: WorkflowTableResponse) =>
        response.data.filter(w => w.ab_test_variants?.length),
    }),
    resetAssistEventConfig: builder.mutation<EventConfigResponse, void>({
      invalidatesTags: ['AssistActionClassification'],
      query: () => ({
        method: 'POST',
        url: ASSIST_INSIGHTS_BASE + '/event-config/reset',
      }),
    }),
    submitConversationReview: builder.mutation<
      ConversationReviewResponse,
      ConversationReviewRequest
    >({
      // A submitted review feeds improved-policy generation eligibility (it
      // classifies asynchronously), so mark every cached signal count stale.
      // The error guard deliberately covers ALL tags, including the
      // pre-existing ConversationReview ones: a failed PUT changed nothing
      // server-side, so there is nothing to refetch.
      invalidatesTags: (_result, error, { conversation_id, is_submitted }) =>
        error
          ? []
          : [
              { id: conversation_id, type: 'ConversationReview' },
              { id: 'LIST', type: 'ConversationReview' },
              ...(is_submitted
                ? (['ImprovedPolicyGenerationEligibility'] as const)
                : []),
            ],
      query: ({ comment, conversation_id, is_submitted, rating }) => ({
        body: { comment, is_submitted, rating },
        method: 'PUT',
        url: `${INSIGHTS_ROUTER_BASE}/conversation-reviews/${conversation_id}`,
      }),
    }),
    testAiQaConfig: builder.mutation<
      AgentQaConfigurationTestResponse,
      AiQaConfigurationTestPayloadRequest
    >({
      query: params => ({
        body: params,
        method: 'POST',
        url: INSIGHTS_ROUTER_BASE + '/ai-qa-configuration-test',
      }),
    }),
    updateAgentQaConfigRank: builder.mutation<
      void,
      AgentQaConfigurationRankRequest
    >({
      invalidatesTags: ['AgentQaConfigs', 'AgentQaSettings'],
      query: params => ({
        body: params,
        method: 'PUT',
        url: `${INSIGHTS_ROUTER_BASE}/agent-qa/configuration-rank`,
      }),
    }),
    updateAgentQaScorecardSettings: builder.mutation<
      void,
      AgentQaScorecardUpdateRequest
    >({
      invalidatesTags: ['AgentQaSettings'],
      query: params => ({
        body: params,
        method: 'PUT',
        url: `${INSIGHTS_ROUTER_BASE}/agent-qa/settings-scorecard`,
      }),
    }),
    updateAiQaConfig: builder.mutation<void, AiQaConfigurationPayloadRequest>({
      invalidatesTags: ['AgentQaConfigs'],
      query: params => ({
        body: params,
        method: 'PUT',
        url: `${INSIGHTS_ROUTER_BASE}/ai-qa-configuration/${params.model_id}`,
      }),
    }),
    updateAiQaConfigRank: builder.mutation<void, AiQaConfigurationsV2Request>({
      invalidatesTags: ['AgentQaConfigs', 'AgentQaSettings'],
      query: params => ({
        body: params,
        method: 'PUT',
        url: `${INSIGHTS_ROUTER_BASE}/ai-qa-configuration-rank`,
      }),
    }),
    updateAiQaSettingsColorThreshold: builder.mutation<
      void,
      AiQaSettingsColorThresholdRequest
    >({
      invalidatesTags: ['AgentQaSettings'],
      query: params => ({
        body: params,
        method: 'PUT',
        url: `${INSIGHTS_ROUTER_BASE}/ai-qa-settings-color-threshold`,
      }),
    }),
    updateAssistEventConfig: builder.mutation<
      EventConfigResponse,
      UpdateEventConfigRequest
    >({
      invalidatesTags: ['AssistActionClassification'],
      query: body => ({
        body,
        method: 'PUT',
        url: ASSIST_INSIGHTS_BASE + '/event-config',
      }),
    }),
  }),
});

export const {
  useCreateAiQaConfigMutation,
  useDeleteAiQaConfigMutation,
  useGetAgentAiMetricCardsQuery,
  useGetAgentAiMetricCardsV2Query,
  useGetAgentAiMetricReasonsQuery,
  useGetAgentQaAccessQuery,
  useGetAgentsHeaderQuery,
  useGetAgentsPerformanceChartQuery,
  useGetAgentsPerformanceChartV2Query,
  useGetAgentsTicketsTableQuery,
  useGetAgentTicketMetadataKeyOptionsQuery,
  useGetAgentTicketMetadataValueOptionsQuery,
  useGetAgentTicketsTableQuery,
  useGetAgentTopicsQuery,
  useGetAiQaConfigsQuery,
  useGetAiQaInsightCardsQuery,
  useGetAiQaSettingsQuery,
  useGetAiReportSummaryQuery,
  useGetArticleDetailGraphQuery,
  useGetArticleDetailHeaderQuery,
  useGetArticleIntegrationOptionsQuery,
  useGetArticlePerformanceQuery,
  useGetArticleQuery,
  useGetArticleTableQuery,
  useGetArticleUserFeedbackQuery,
  useGetAssistActionRecordsQuery,
  useGetAssistAgentEngagementTrendsQuery,
  useGetAssistAgentInfoQuery,
  useGetAssistAgentOutcomeTrendsQuery,
  useGetAssistAgentStatsQuery,
  useGetAssistAvailableEventsQuery,
  useGetAssistCardBuilderOptionsQuery,
  useGetAssistConversationDetailQuery,
  useGetAssistConversationsQuery,
  useGetAssistConversationTableQuery,
  useGetAssistEventConfigQuery,
  useGetAssistFcrImpactQuery,
  useGetAssistFeedbackQuery,
  useGetAssistFirstResponseTimeImpactQuery,
  useGetAssistGeneratedReplyQuery,
  useGetAssistInteractionsQuery,
  useGetAssistOverviewAgentsQuery,
  useGetAssistOverviewArticlesQuery,
  useGetAssistOverviewFilterOptionsQuery,
  useGetAssistOverviewQuery,
  useGetAssistRepliesImpactQuery,
  useGetAssistResolutionTimeImpactQuery,
  useGetAssistTicketTableQuery,
  useGetChannelOptionsQuery,
  useGetChatsSavingsQuery,
  useGetCommonCSATQuery,
  useGetConversationDeeplinkQuery,
  useGetConversationQuery,
  useGetConversationReviewQuery,
  useGetConversationTableQuery,
  useGetCSATQuery,
  useGetEmailArticleTableQuery,
  useGetEmailChatsSavingsQuery,
  useGetEmailConversationTableQuery,
  useGetEmailFeedbackQuery,
  useGetEmailHelpdeskCsatQuery,
  useGetEmailNoResponseQuery,
  useGetExcludedRubricsQuery,
  useGetGeneratedArticlesMutation,
  useGetHelpdeskGroupOptionsQuery,
  useGetKrWorkflowTableQuery,
  useGetOrgFeatureQaConfigQuery,
  useGetOverallCSATQuery,
  useGetOverviewChatPerformanceQuery,
  useGetOverviewChatsChartQuery,
  useGetOverviewCsatChartQuery,
  useGetOverviewDeflectedChartQuery,
  useGetOverviewEngagementChartQuery,
  useGetOverviewKASurfacedChartQuery,
  useGetOverviewOvertimeChartQuery,
  useGetOverviewQuickFeedbackChartQuery,
  useGetOverviewRelevanceChartQuery,
  useGetProactiveConversationsQuery,
  useGetProactiveFunnelQuery,
  useGetQuickFeedbackQuery,
  useGetReviewProgressQuery,
  useGetScheduledOutboundCallsTableQuery,
  useGetSentimentInsightQuery,
  useGetTicketChannelOptionsQuery,
  useGetTicketStatusFilterOptionsQuery,
  useGetTopicCardQuery,
  useGetTopicDetailGraphQuery,
  useGetTopicFilterOptionsQuery,
  useGetTopicHeaderQuery,
  useGetTopicInformationQuery,
  useGetTopicsEngagementQuery,
  useGetTopicsRelevanceQuery,
  useGetTopicsTableQuery,
  useGetWorkflowDetailChartQuery,
  useGetWorkflowDetailHeaderQuery,
  useGetWorkflowFilterOptionsQuery,
  useGetWorkflowOverallCSATQuery,
  useGetWorkflowOverallDeflectionsQuery,
  useGetWorkflowOverallEngagementQuery,
  useGetWorkflowOverallRelevanceQuery,
  useGetWorkflowTableQuery,
  useGetWorkflowVariantMetricsQuery,
  useLazyGetConversationTableQuery,
  useResetAssistEventConfigMutation,
  useSubmitConversationReviewMutation,
  useTestAiQaConfigMutation,
  useUpdateAgentQaConfigRankMutation,
  useUpdateAgentQaScorecardSettingsMutation,
  useUpdateAiQaConfigMutation,
  useUpdateAiQaConfigRankMutation,
  useUpdateAiQaSettingsColorThresholdMutation,
  useUpdateAssistEventConfigMutation,
} = insightsApi;
